// src/pages/TuckShop/LoginPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Card, CardContent, TextField, Typography, Button, Avatar,
  Grid, IconButton, InputAdornment, Stack, Alert, AlertTitle,
  Paper, alpha, Chip, useMediaQuery, useTheme, Divider,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';

import logo from '../assets/logo.jpg';
import accountImage from '../assets/ACCE.jpg';
import Preloader from '../theme/Preloader';
import { getRedirectPathByRole } from '../utility/roleRedirect';
import { ACCE_BRAND } from '../theme/colors';

// ════════════════════════════════════════════
// LOGGER
// ════════════════════════════════════════════
const logger = {
  info:  (m, d) => console.log(`[SCHOOL LOGIN] ${m}`, d || ''),
  error: (m, e) => console.error(`[SCHOOL LOGIN] ${m}`, e || ''),
  debug: (m, d) => console.debug(`[SCHOOL LOGIN] ${m}`, d || ''),
  warn:  (m, d) => console.warn(`[SCHOOL LOGIN] ${m}`, d || ''),
};

// ════════════════════════════════════════════
// SCHOOL CONFIG — pulled from .env
// ════════════════════════════════════════════
const STORE_CONFIG = {
  orgName:        process.env.REACT_APP_ORGANISATION_NAME      || 'ACCE Abuja',
  tuckName:       process.env.REACT_APP_TUCK_NAME              || 'ACCE Abuja',
  address:        process.env.REACT_APP_TUCK_ADDRESS           || 'Plot 81 Kafe District, Gwarinpa Estate, Abuja',
  phone:          process.env.REACT_APP_TUCK_PHONE             || '+234 09070596302',
  email:          process.env.REACT_APP_TUCK_EMAIL             || 'info@acce-abuja.com',
  website:        process.env.REACT_APP_TUCK_WEBSITE           || 'https://www.acce-abuja.com/',
  slogan:         process.env.REACT_APP_TUCK_SLOGAN            || 'Inspiring Excellence',
  currency:       process.env.REACT_APP_TUCK_CURRENCY          || '₦',
  country:        process.env.REACT_APP_TUCK_COUNTRY           || 'Nigeria',
  city:           process.env.REACT_APP_TUCK_CITY              || 'Gwarimpa',
  state:          process.env.REACT_APP_TUCK_STATE             || 'Abuja',
  operatingHours: process.env.REACT_APP_TUCK_OPERATING_HOURS   || 'Mon-Fri: 7:30AM',
  supportPhone:   process.env.REACT_APP_TUCK_SUPPORT_PHONE     || '+234 8065514429',
};

const WEBSITE_DISPLAY = STORE_CONFIG.website.replace(/^https?:\/\//, '').replace(/\/$/, '');

// ════════════════════════════════════════════
// API CONFIG
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

logger.info('API Configuration', { API_BASE_URL, API_PREFIX, FULL_API_URL });

// ════════════════════════════════════════════
// AXIOS INSTANCE
// ════════════════════════════════════════════
const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.debug('Token attached', { url: config.url });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        clearAuthData();
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
          `${FULL_API_URL}/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${refreshToken}` } }
        );

        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);

        processQueue(null, data.access_token);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthData();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ════════════════════════════════════════════
// STORAGE HELPERS
// ════════════════════════════════════════════
const clearAuthData = () => {
  [
    'access_token', 'refresh_token', 'user_data', 'user_email', 'user_role',
    'user_id', 'user_name', 'first_name', 'staff_id', 'department', 'phone',
    'gender', 'is_active', 'user_location',
    'email', 'role', 'userId', 'userName', 'staffId', 'userDepartment', 'phoneNumber',
  ].forEach((k) => localStorage.removeItem(k));
  logger.info('Auth data cleared');
};

const storeUserData = (user) => {
  if (!user) return;
  localStorage.setItem('user_data', JSON.stringify(user));
  localStorage.setItem('user_email', user.email);
  localStorage.setItem('user_role', user.role);
  localStorage.setItem('user_id', String(user.id));
  localStorage.setItem('user_name', `${user.first_name} ${user.other_names || ''}`.trim());
  localStorage.setItem('first_name', user.first_name || '');
  localStorage.setItem('staff_id', user.staff_id || '');
  localStorage.setItem('department', user.department || '');
  localStorage.setItem('phone', user.phone || '');
  localStorage.setItem('gender', user.gender || '');
  localStorage.setItem('is_active', user.is_active ? 'true' : 'false');
  localStorage.setItem('user_location', user.location || '');

  localStorage.setItem('email', user.email);
  localStorage.setItem('role', user.role);
  localStorage.setItem('userId', String(user.id));
  localStorage.setItem('userName', `${user.first_name} ${user.other_names || ''}`.trim());
  if (user.staff_id)   localStorage.setItem('staffId', user.staff_id);
  if (user.department) localStorage.setItem('userDepartment', user.department);
  if (user.phone)      localStorage.setItem('phoneNumber', user.phone);

  logger.debug('User data stored', { email: user.email, role: user.role });
};

const isAuthenticated = () =>
  !!(localStorage.getItem('access_token') && localStorage.getItem('user_data'));
const getUserRole     = () => localStorage.getItem('user_role') || null;
const getUserLocation = () => localStorage.getItem('user_location') || '';

// ════════════════════════════════════════════
// LOGIN FUNCTION
// ════════════════════════════════════════════
const loginUser = async (email, password) => {
  try {
    logger.info('Login attempt', { email });
    const { data } = await api.post('/auth/login-json', { email, password });

    if (!data?.access_token || !data?.user) {
      return { success: false, error: 'Invalid response from server' };
    }

    localStorage.setItem('access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    storeUserData(data.user);

    logger.info('Login successful', { email: data.user.email, role: data.user.role });
    return { success: true, user: data.user, tokens: data };
  } catch (error) {
    logger.error('Login failed', error);
    let message = 'Login failed. Please try again.';

    if (error.response) {
      const d = error.response.data;
      message = d?.errors?.email?.[0] || d?.errors?.password?.[0] || d?.message || message;
      const status = error.response.status;
      if (status === 401) message = 'Invalid email or password';
      else if (status === 403) message = 'Account is deactivated';
      else if (status === 422) message = message || 'Validation error';
      else if (status >= 500) message = 'Server error. Try again later.';
      logger.error('Login error details', { status, data: d });
    } else if (error.request) {
      message = 'Network error. Check your connection.';
    }

    return { success: false, error: message };
  }
};

// ════════════════════════════════════════════
// SEQUENTIAL TYPEWRITER HOOK
// Types multiple strings one after the other (name → slogan)
// ════════════════════════════════════════════
const useSequentialTypewriter = (
  lines,
  { speed = 65, lineDelay = 400, startDelay = 350, enabled = true } = {}
) => {
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [displayed, setDisplayed]               = useState('');
  const [isDone, setIsDone]                     = useState(false);

  useEffect(() => {
    if (!enabled || !lines || lines.length === 0) {
      setIsDone(true);
      return;
    }

    setCurrentLineIndex(0);
    setDisplayed('');
    setIsDone(false);

    let lineIdx = 0;
    let charIdx = 0;
    let timeoutId = null;

    const typeNextChar = () => {
      const line = lines[lineIdx];

      if (charIdx < line.length) {
        charIdx += 1;
        setDisplayed(line.slice(0, charIdx));
        timeoutId = setTimeout(typeNextChar, speed);
      } else {
        if (lineIdx < lines.length - 1) {
          timeoutId = setTimeout(() => {
            lineIdx += 1;
            charIdx = 0;
            setCurrentLineIndex(lineIdx);
            setDisplayed('');
            typeNextChar();
          }, lineDelay);
        } else {
          setIsDone(true);
        }
      }
    };

    const startTimeout = setTimeout(typeNextChar, startDelay);

    return () => {
      clearTimeout(startTimeout);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [lines, speed, lineDelay, startDelay, enabled]);

  return { currentLineIndex, displayed, isDone };
};

// ════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════
const TuckShop_LoginPage = () => {
  const [formData, setFormData]             = useState({ email: '', password: '' });
  const [errors, setErrors]                 = useState({ email: '', password: '' });
  const [serverError, setServerError]       = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword]     = useState(false);
  const [isLoading, setIsLoading]           = useState(false);
  const [isPageLoading, setIsPageLoading]   = useState(true);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile      = useMediaQuery(theme.breakpoints.down('md'));
  const isShortScreen = useMediaQuery('(max-height: 800px)');
  const hasRedirected = useRef(false);

  const {
    navy, navyLight,
    orange, orangeLight,
    gray,
    white, offWhite, bodyText, mutedText,
    navyGradient, orangeGradient, accentBar,
    softBackdrop,
  } = ACCE_BRAND;

  // ── Sequential typewriter: name → slogan ──
  const typewriterLines = React.useMemo(
    () => [STORE_CONFIG.orgName, STORE_CONFIG.slogan],
    []
  );

  const {
    currentLineIndex: typedLine,
    displayed: typedText,
    isDone: typingComplete,
  } = useSequentialTypewriter(typewriterLines, {
    speed: 70,
    lineDelay: 500,
    startDelay: 350,
    enabled: !isPageLoading,
  });

  // ── Auto-redirect if already logged in ──
 useEffect(() => {
  if (hasRedirected.current) return;
  if (isAuthenticated()) {
    const role = getUserRole();
    const loc  = getUserLocation();
    const path = getRedirectPathByRole(role, loc);

    // If role has no valid destination, clear auth and stay on login
    if (path === '/') {
      logger.warn('Unsupported role, clearing session', { role });
      clearAuthData();
      hasRedirected.current = true;
      setIsPageLoading(false);
      return;
    }

    logger.info('Already authenticated, redirecting', { role, path });
    hasRedirected.current = true;
    navigate(path, { replace: true });
    return;
  }
  setIsPageLoading(false);
}, [navigate]);

  // ── Validation ──
  const validate = () => {
    const e = { email: '', password: '' };
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) e.email = 'Email is required';
    else if (!re.test(formData.email)) e.email = 'Enter a valid email address';
    if (!formData.password) e.password = 'Password is required';
    else if (formData.password.length < 6) e.password = 'Password must be at least 6 characters';
    setErrors(e);
    return !e.email && !e.password;
  };

  const handleChange = (e) => {
    setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
    setServerError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || isLoading) return;

    setIsLoading(true);
    setServerError('');
    setSuccessMessage('');

    try {
      const result = await loginUser(formData.email, formData.password);
      if (!result.success) {
        setServerError(result.error);
        setIsLoading(false);
        return;
      }

      const role = result.user?.role || 'user';
      const loc  = result.user?.location || '';
      const path = getRedirectPathByRole(role, loc);

      // Unsupported role — do NOT navigate. Clear session and stay on login.
      if (path === '/') {
        logger.warn('Unsupported role at login, clearing session', { role });
        clearAuthData();
        setServerError('Your role does not have portal access yet. Please contact ICT.');
        setSuccessMessage('');
        setIsLoading(false);
        return;
      }

      setSuccessMessage('Login successful!');
      logger.info('Redirecting', { role, location: loc, path });

      setTimeout(() => navigate(path, { replace: true }), 300);
    } catch (err) {
      logger.error('Unexpected error', err);
      setServerError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((s) => !s);

  const features = [
    { icon: <MenuBookOutlinedIcon   sx={{ fontSize: 14 }} />, text: 'Curriculum' },
    { icon: <InsightsOutlinedIcon   sx={{ fontSize: 14 }} />, text: 'Analytics' },
    { icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 14 }} />, text: 'Secure Access' },
    { icon: <BadgeOutlinedIcon      sx={{ fontSize: 14 }} />, text: 'Staff Portal' },
  ];

  if (isPageLoading) return <Preloader text="Loading Portal..." size={70} fullScreen />;
  if (isLoading)     return <Preloader text="Signing you in..." size={70} fullScreen />;

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════
  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        maxWidth: '100vw',
        display: 'flex',
        m: 0, p: 0,
        background: softBackdrop,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ═══════ BACKGROUND LAYER ═══════ */}
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, ${alpha(navy, 0.14)} 0%, ${alpha(navy, 0.04)} 40%, ${alpha(orange, 0.14)} 100%)`,
        }} />

        <Box sx={{
          position: 'absolute', inset: 0,
          background: `
            linear-gradient(135deg, ${alpha(navy, 0.14)} 0%, transparent 50%),
            linear-gradient(315deg, ${alpha(orange, 0.16)} 0%, transparent 55%)
          `,
        }} />

        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            linear-gradient(${alpha(navy, 0.12)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(navy, 0.12)} 1px, transparent 1px)
          `,
          backgroundSize: '44px 44px',
          maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 95%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 95%)',
        }} />

        <Box sx={{
          position: 'absolute', top: '-15%', right: '-10%', width: '75%', height: '75%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(orange, 0.42)} 0%, ${alpha(orange, 0.12)} 45%, transparent 70%)`,
          animation: 'floatOrb 22s ease-in-out infinite',
          filter: 'blur(10px)',
        }} />

        <Box sx={{
          position: 'absolute', bottom: '-20%', left: '-10%', width: '65%', height: '65%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(navy, 0.40)} 0%, ${alpha(navy, 0.12)} 45%, transparent 70%)`,
          animation: 'floatOrb 26s ease-in-out infinite reverse',
          filter: 'blur(10px)',
        }} />

        <Box sx={{
          position: 'absolute', top: '25%', left: '30%', width: '50%', height: '50%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(orangeLight, 0.28)} 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }} />

        <Box sx={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(ellipse at center, transparent 50%, ${alpha(navy, 0.18)} 100%)`,
        }} />

        <style>{`
          @keyframes floatOrb {
            0%,100% { transform: translate(0,0) scale(1); }
            25%     { transform: translate(50px,-50px) scale(1.12); }
            50%     { transform: translate(-40px,-70px) scale(1); }
            75%     { transform: translate(40px,-40px) scale(1.06); }
          }
          @keyframes pulseRing {
            0%   { transform: scale(1);   opacity: 0.5; }
            100% { transform: scale(1.5); opacity: 0; }
          }
          @keyframes glossShine {
            0%   { transform: translateX(-100%) skewX(-20deg); }
            100% { transform: translateX(200%) skewX(-20deg); }
          }
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes blinkCursor {
            0%, 49%   { opacity: 1; }
            50%, 100% { opacity: 0; }
          }
        `}</style>
      </Box>

      {/* ═══════ MAIN SPLIT ═══════ */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          width: '100vw',
          minHeight: '100vh',
        }}
      >
        {/* ───────── LEFT — Institutional panel ───────── */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            width: { md: '58%', lg: '60%', xl: '62%' },
            flexShrink: 0,
            minHeight: '100vh',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              p: { md: 4, lg: 5, xl: 6 },
              borderRadius: 0,
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              background: `
                linear-gradient(135deg, ${alpha(navy, 0.78)} 0%, ${alpha(navy, 0.62)} 45%, ${alpha(navy, 0.85)} 100%),
                url(${accountImage}) center/cover no-repeat,
                ${navyGradient}
              `,
              boxShadow: '0 20px 60px rgba(42,58,84,0.25), 0 8px 20px rgba(42,58,84,0.12)',
            }}
          >
            <Box sx={{
              position: 'absolute', top: '-20%', right: '-20%',
              width: '60%', height: '60%',
              background: `radial-gradient(circle, ${alpha(orange, 0.14)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }} />
            <Box sx={{
              position: 'absolute', bottom: '-20%', left: '-20%',
              width: '60%', height: '60%',
              background: `radial-gradient(circle, ${alpha(orangeLight, 0.08)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }} />

            <Box sx={{
              position: 'absolute', top: 0, left: 0, width: 5, height: '100%',
              background: orangeGradient,
              borderRadius: '0 3px 3px 0',
              zIndex: 2,
              boxShadow: `0 0 20px ${alpha(orange, 0.6)}`,
            }} />

            <Box sx={{
              position: 'relative', zIndex: 3,
              display: 'flex', flexDirection: 'column', justifyContent: 'center',
              minHeight: '100%',
              animation: 'fadeUp 0.7s ease-out',
            }}>
              {/* Logo */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: { md: 1.5, lg: 2 } }}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: '50%',
                    background: alpha(white, 0.98),
                    boxShadow: `0 10px 32px ${alpha('#000', 0.32)}, inset 0 1px 0 ${alpha(white, 0.7)}`,
                    border: `2px solid ${alpha(orange, 0.45)}`,
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: `0 16px 42px ${alpha('#000', 0.38)}, inset 0 1px 0 ${alpha(white, 0.9)}, 0 0 0 6px ${alpha(orange, 0.12)}`,
                    },
                  }}
                >
                  <Box component="img" src={logo} alt={`${STORE_CONFIG.orgName} Logo`}
                    sx={{ width: { md: 78, lg: 88, xl: 96 }, height: 'auto', display: 'block', borderRadius: '50%' }} />
                </Box>
              </Box>

              {/* School name + slogan — sequential typing with shared cursor */}
              <Box sx={{ textAlign: 'center', mb: { md: 2, lg: 2.5 } }}>
                {/* Line 1: Name — types out */}
                <Typography
                  variant="h5"
                  sx={{
                    color: white,
                    fontWeight: 800,
                    fontSize: { md: '1.25rem', lg: '1.4rem', xl: '1.55rem' },
                    letterSpacing: '-0.3px',
                    mb: 0.5,
                    textShadow: `0 2px 16px ${alpha('#000', 0.55)}`,
                    minHeight: '1.5em',
                  }}
                >
                  {typedLine === 0 ? typedText : STORE_CONFIG.orgName}
                  {typedLine === 0 && !typingComplete && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '2px',
                        height: '1em',
                        ml: '2px',
                        verticalAlign: 'text-bottom',
                        backgroundColor: orange,
                        boxShadow: `0 0 8px ${alpha(orange, 0.9)}`,
                        animation: 'blinkCursor 0.9s step-end infinite',
                      }}
                    />
                  )}
                </Typography>

                {/* Line 2: Slogan — types out after name */}
                <Typography
                  variant="body2"
                  sx={{
                    color: orangeLight,
                    fontWeight: 600,
                    fontSize: { md: '0.78rem', lg: '0.86rem' },
                    letterSpacing: 2.5,
                    textTransform: 'uppercase',
                    mb: 1.25,
                    textShadow: `0 2px 10px ${alpha('#000', 0.5)}`,
                    minHeight: '1.4em',
                  }}
                >
                  {typedLine === 1 ? typedText : (typedLine > 1 || typingComplete ? STORE_CONFIG.slogan : '')}
                  {typedLine === 1 && !typingComplete && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '2px',
                        height: '1em',
                        ml: '2px',
                        verticalAlign: 'text-bottom',
                        backgroundColor: orange,
                        boxShadow: `0 0 8px ${alpha(orange, 0.9)}`,
                        animation: 'blinkCursor 0.9s step-end infinite',
                      }}
                    />
                  )}
                </Typography>

                {/* Divider — no animation, always visible */}
                <Divider
                  sx={{
                    width: 48, mx: 'auto',
                    borderColor: alpha(orange, 0.7),
                    borderBottomWidth: 2,
                    mb: 1.75,
                  }}
                />

                {/* Caption — no animation */}
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(white, 0.85),
                    fontSize: { md: '0.68rem', lg: '0.72rem' },
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    textShadow: `0 2px 10px ${alpha('#000', 0.55)}`,
                    display: 'inline-block',
                  }}
                >
                  Official Staff &amp; Students Portal
                </Typography>
              </Box>

              {/* Feature chips — no animation */}
              <Stack
                direction="row" spacing={0.75} flexWrap="wrap" useFlexGap
                justifyContent="center"
                sx={{ mb: { md: 2, lg: 2.5 } }}
              >
                {features.map((f, i) => (
                  <Chip key={i} icon={f.icon} label={f.text} size="small"
                    sx={{
                      backgroundColor: alpha(white, 0.14),
                      backdropFilter: 'blur(10px)',
                      color: white,
                      border: `1px solid ${alpha(white, 0.22)}`,
                      fontWeight: 500,
                      fontSize: { md: '0.68rem', lg: '0.72rem' },
                      height: 26, mb: 0.5,
                      transition: 'all 0.3s ease',
                      '& .MuiChip-icon': { color: orangeLight },
                      '&:hover': {
                        backgroundColor: alpha(orange, 0.28),
                        borderColor: alpha(orange, 0.5),
                        transform: 'translateY(-2px)',
                      },
                    }}
                  />
                ))}
              </Stack>

              {/* Contact block — no animation */}
              <Box
                sx={{
                  pt: 2.5,
                  mt: 'auto',
                  borderTop: `1px solid ${alpha(white, 0.22)}`,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.85,
                    maxWidth: { md: 360, lg: 400, xl: 440 },
                    width: '100%',
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                    <SchoolOutlinedIcon sx={{ fontSize: 15, color: orange }} />
                  </Box>

                  <ContactRow icon={<LocationOnIcon sx={{ fontSize: 14 }} />} text={STORE_CONFIG.address} />
                  <ContactRow icon={<PhoneIcon sx={{ fontSize: 14 }} />}      text={`${STORE_CONFIG.phone}  ·  Support: ${STORE_CONFIG.supportPhone}`} />
                  <ContactRow icon={<EmailIcon sx={{ fontSize: 14 }} />}      text={STORE_CONFIG.email} />
                  <ContactRow icon={<AccessTimeIcon sx={{ fontSize: 14 }} />} text={STORE_CONFIG.operatingHours} />
                  <ContactRow icon={<LanguageIcon sx={{ fontSize: 14 }} />}   text={WEBSITE_DISPLAY} />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* ───────── RIGHT — Frosted glass login card ───────── */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: { xs: 'center', md: 'flex-end' },
            minHeight: '100vh',
            minWidth: 0,
            pl: 0,
            pr: 0,
          }}
        >
          <Card
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: { md: 480, lg: 540, xl: 580 },
              borderRadius: 0,
              background: `linear-gradient(135deg, ${alpha(white, 0.72)} 0%, ${alpha(offWhite, 0.60)} 100%)`,
              backdropFilter: 'blur(18px) saturate(140%)',
              WebkitBackdropFilter: 'blur(18px) saturate(140%)',
              border: 'none',
              borderLeft: { md: `1px solid ${alpha(navy, 0.08)}` },
              boxShadow: '-20px 0 60px rgba(42,58,84,0.10), -8px 0 20px rgba(42,58,84,0.05)',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: '100vh',
              p: {
                xs: 2.5,
                sm: 3.5,
                md: isShortScreen ? 3 : 4,
                lg: isShortScreen ? 4 : 5,
                xl: isShortScreen ? 4 : 5,
              },
            }}
          >
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: accentBar }}>
              <Box sx={{
                position: 'absolute', top: 0, left: 0, width: '40%', height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)',
                animation: 'glossShine 4s ease-in-out infinite',
              }} />
            </Box>

            <CardContent sx={{
              p: '0 !important', pt: '8px !important', width: '100%',
              maxWidth: 480, mx: 'auto',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center',
              animation: 'fadeUp 0.6s ease-out 0.1s both',
            }}>
              {isMobile && (
                <Box sx={{
                  display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2,
                  p: 1.25, borderRadius: '50%',
                  background: alpha(white, 0.85),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(navy, 0.08)}`,
                }}>
                  <Box component="img" src={logo} alt={`${STORE_CONFIG.orgName} Logo`}
                    sx={{ width: { xs: 72, sm: 82 }, height: 'auto', display: 'block', borderRadius: '50%' }} />
                </Box>
              )}

              <Box sx={{ position: 'relative', mb: isShortScreen ? 1 : 1.5 }}>
                <Box sx={{
                  position: 'absolute', inset: 0,
                  borderRadius: '50%',
                  border: `2px solid ${alpha(orange, 0.4)}`,
                  animation: 'pulseRing 2.5s ease-out infinite',
                }} />
                <Avatar
                  sx={{
                    width: { xs: 54, sm: 58, md: isShortScreen ? 54 : 62 },
                    height: { xs: 54, sm: 58, md: isShortScreen ? 54 : 62 },
                    background: navyGradient,
                    boxShadow: `0 8px 28px ${alpha(navy, 0.32)}, inset 0 2px 4px rgba(255,255,255,0.2), inset 0 -2px 4px rgba(0,0,0,0.2), 0 0 0 4px ${alpha(orange, 0.12)}`,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '8%', left: '15%',
                      width: '40%', height: '30%',
                      background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.45) 0%, transparent 70%)',
                      borderRadius: '50%',
                      pointerEvents: 'none',
                    },
                  }}
                >
                  <SchoolOutlinedIcon sx={{
                    fontSize: { xs: 24, sm: 26, md: isShortScreen ? 24 : 28 },
                    color: orange,
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                  }} />
                </Avatar>
              </Box>

              <Typography variant="h4" gutterBottom
                sx={{
                  fontWeight: 800, color: navy,
                  fontSize: { xs: '1.35rem', sm: '1.5rem', md: isShortScreen ? '1.45rem' : '1.65rem', lg: isShortScreen ? '1.55rem' : '1.8rem' },
                  textAlign: 'center',
                  letterSpacing: '-0.5px', mb: 0.5,
                  textShadow: `0 1px 2px ${alpha(white, 0.6)}`,
                }}>
                Welcome Back
              </Typography>

              <Typography variant="body2" sx={{
                color: bodyText,
                fontSize: { xs: '0.82rem', sm: '0.88rem', md: '0.92rem' },
                textAlign: 'center',
                mb: isShortScreen ? 1.5 : 2,
                maxWidth: 420,
                textShadow: `0 1px 1px ${alpha(white, 0.5)}`,
              }}>
                Sign in to access the ACCE Portal
              </Typography>

              <Stack
                direction="row" spacing={0.75} flexWrap="wrap" useFlexGap
                justifyContent="center"
                sx={{ mb: isShortScreen ? 1.5 : 2 }}
              >
                {[
                  { icon: <ShieldOutlinedIcon sx={{ fontSize: 12 }} />, text: 'Encrypted' },
                  { icon: <SpeedOutlinedIcon  sx={{ fontSize: 12 }} />, text: 'Fast' },
                  { icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 12 }} />, text: 'Verified' },
                ].map((item, i) => (
                  <Chip key={i} icon={item.icon} label={item.text} size="small"
                    sx={{
                      backgroundColor: alpha(white, 0.6),
                      backdropFilter: 'blur(8px)',
                      color: navy,
                      border: `1px solid ${alpha(navy, 0.12)}`,
                      fontWeight: 600, fontSize: '0.65rem', height: 22,
                      '& .MuiChip-icon': { color: orange, fontSize: 12 },
                    }}
                  />
                ))}
              </Stack>

              {serverError && (
                <Alert severity="error" sx={{
                  mb: 1.5, width: '100%', maxWidth: 480, borderRadius: 2, py: 0.5,
                  backgroundColor: alpha('#C62828', 0.10),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha('#C62828', 0.25)}`,
                  '& .MuiAlert-message': { fontSize: '0.85rem' },
                }}>
                  <AlertTitle sx={{ fontSize: '0.9rem', mb: 0.5, fontWeight: 700 }}>Access Denied</AlertTitle>
                  {serverError}
                </Alert>
              )}
              {successMessage && (
                <Alert severity="success" sx={{
                  mb: 1.5, width: '100%', maxWidth: 480, borderRadius: 2, py: 0.5,
                  backgroundColor: alpha('#2E7D32', 0.10),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha('#2E7D32', 0.25)}`,
                  '& .MuiAlert-message': { fontSize: '0.85rem' },
                }}>
                  <AlertTitle sx={{ fontSize: '0.9rem', mb: 0.5, fontWeight: 700 }}>Welcome Back!</AlertTitle>
                  {successMessage}
                </Alert>
              )}

              <Box component="form" noValidate onSubmit={handleSubmit}
                sx={{ width: '100%', maxWidth: 480, mx: 'auto' }}>

                <TextField
                  placeholder="Staff Email Address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  type="email"
                  autoComplete="email"
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon sx={{ color: orange, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: alpha(white, 0.55),
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      transition: 'all 0.3s ease',
                      padding: '2px 0',
                      fontSize: '0.95rem',
                      '& fieldset': { borderColor: alpha(navy, 0.15) },
                      '&:hover': {
                        backgroundColor: alpha(white, 0.75),
                        boxShadow: `0 2px 8px ${alpha(navy, 0.06)}`,
                        '& fieldset': { borderColor: alpha(orange, 0.5) },
                      },
                      '&.Mui-focused': {
                        backgroundColor: white,
                        boxShadow: `0 4px 18px ${alpha(orange, 0.15)}`,
                        '& fieldset': { borderColor: orange, borderWidth: 2 },
                      },
                      '& input': { padding: '14px 12px', fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } },
                    },
                    '& .MuiInputLabel-root': { display: 'none' },
                    '& .MuiFormHelperText-root': { marginLeft: '8px', fontSize: '0.72rem', mt: 0.5 },
                  }}
                />

                <TextField
                  placeholder="Password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: orange, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                          size="small"
                          sx={{ color: alpha(navy, 0.5), '&:hover': { color: orange } }}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: alpha(white, 0.55),
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      transition: 'all 0.3s ease',
                      padding: '2px 0',
                      fontSize: '0.95rem',
                      '& fieldset': { borderColor: alpha(navy, 0.15) },
                      '&:hover': {
                        backgroundColor: alpha(white, 0.75),
                        boxShadow: `0 2px 8px ${alpha(navy, 0.06)}`,
                        '& fieldset': { borderColor: alpha(orange, 0.5) },
                      },
                      '&.Mui-focused': {
                        backgroundColor: white,
                        boxShadow: `0 4px 18px ${alpha(orange, 0.15)}`,
                        '& fieldset': { borderColor: orange, borderWidth: 2 },
                      },
                      '& input': { padding: '14px 12px', fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } },
                    },
                    '& .MuiInputLabel-root': { display: 'none' },
                    '& .MuiFormHelperText-root': { marginLeft: '8px', fontSize: '0.72rem', mt: 0.5 },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isLoading}
                  sx={{
                    mt: isShortScreen ? 1.5 : 2.5,
                    py: { xs: 1.4, sm: 1.5, md: isShortScreen ? 1.3 : 1.6 },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.95rem', sm: '1rem', md: isShortScreen ? '0.95rem' : '1.05rem' },
                    borderRadius: 2,
                    background: navyGradient,
                    color: white,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: `0 8px 28px ${alpha(navy, 0.35)}, inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -2px 6px rgba(0,0,0,0.2)`,
                    letterSpacing: 0.5,
                    border: `1px solid ${alpha(navy, 0.4)}`,
                    '&::before': {
                      content: '""',
                      position: 'absolute', top: 0, left: '-100%',
                      width: '100%', height: '100%',
                      background: `linear-gradient(90deg, transparent, ${alpha(orange, 0.5)}, transparent)`,
                      transition: 'all 0.7s ease',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0, left: 0, right: 0, height: '3px',
                      background: orangeGradient,
                      transition: 'all 0.3s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 36px ${alpha(navy, 0.45)}, 0 0 0 4px ${alpha(orange, 0.12)}, inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 6px rgba(0,0,0,0.2)`,
                      '&::before': { left: '100%' },
                    },
                    '&:active': {
                      transform: 'translateY(0) scale(0.98)',
                      boxShadow: `0 4px 16px ${alpha(navy, 0.4)}, inset 0 2px 6px rgba(0,0,0,0.25)`,
                    },
                    '&.Mui-disabled': {
                      background: alpha(navy, 0.4),
                      color: alpha(white, 0.6),
                    },
                  }}
                  endIcon={!isLoading && <KeyboardArrowRightIcon />}
                >
                  {isLoading ? 'Signing in...' : 'Sign in to Portal'}
                </Button>

                <Box sx={{ textAlign: 'center', mt: isShortScreen ? 2 : 3 }}>
                  <Typography variant="caption" sx={{
                    color: mutedText,
                    fontSize: { xs: '0.68rem', sm: '0.7rem' },
                    display: 'block',
                    mb: 0.5,
                    textShadow: `0 1px 1px ${alpha(white, 0.5)}`,
                  }}>
                    Need help? Contact ICT · {STORE_CONFIG.supportPhone}
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: alpha(mutedText, 0.8),
                    fontSize: { xs: '0.62rem', sm: '0.65rem' },
                    letterSpacing: 0.5,
                    textShadow: `0 1px 1px ${alpha(white, 0.5)}`,
                  }}>
                    Powered by e-Neurons Technologies Ltd
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

// ── Helper for contact rows ──
const ContactRow = ({ icon, text }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.25,
    color: alpha('#FFFFFF', 0.9),
    transition: 'color 0.3s ease',
    '&:hover': { color: ACCE_BRAND.orangeLight },
    width: '100%',
    textShadow: `0 2px 8px ${alpha('#000', 0.55)}`,
  }}>
    <Box
      sx={{
        color: ACCE_BRAND.orange,
        display: 'flex',
        flexShrink: 0,
        width: 18,
        height: 18,
        alignItems: 'center',
        justifyContent: 'center',
        mt: '1px',
      }}
    >
      {icon}
    </Box>
    <Typography variant="caption" sx={{
      fontSize: { md: '0.68rem', lg: '0.72rem' },
      textAlign: 'left',
      wordBreak: 'break-word',
      lineHeight: 1.5,
      flex: 1,
    }}>
      {text}
    </Typography>
  </Box>
);

export default TuckShop_LoginPage;